// Global Instructions Rule Applied!
//
// Fee Booking workbench > Fee history: the fund's fee-period transactions
// (fee__booking_history), newest first, at FUND level. Each row expands to
// the fund-level calculation basis it was booked on (FeeBasisCard).
//
// Source: 'snapshot' = the basis frozen when the transaction was generated
// (ft__generate_management_fee_journals writes it; a Booked transaction never
// regenerates). 'recomputed' = a transaction that predates snapshots, so its
// basis is recalculated with today's terms and may differ from what was
// booked - flagged gold.
//
// Period fee is the engine's fee for the transaction's own window; Amount is
// what the transaction books, which is self-truing (all fees due to date
// minus fees already booked), so True-up = Amount - Period fee surfaces any
// catch-up of earlier periods.
//
// Drift: a snapshot row also carries today's recompute of the same window
// (current_basis / prog__current_period_fee / prog__drift). When terms have
// changed since the fee was calculated the row gets a gold "Terms changed"
// tag and the expanded row compares then vs today - the difference is what
// the next self-truing booking will book as a true-up.

import React, { useMemo } from 'react'
import { Table, Tag, Tooltip, Typography } from 'antd'
import dayjs from 'dayjs'
import { FormSection } from '../../../core/components/FormSection.tsx'
import NumberText from '../../../core/components/NumberText.tsx'
import { FeeBasis, FeeHistoryRow } from '../../../state_manager/data_models/fee_basis.ts'
import FeeBasisCard, { formatPercent } from './FeeBasisCard.tsx'
import ExportRowsButton from '../../shared/table_grid_export/ExportRowsButton.tsx'

const { Text } = Typography

const DATE_FORMAT = 'MM/DD/YYYY'

// "Q4 2025" / "H1 2025" / "2025" when the window is a whole calendar
// quarter / half / year, else the date range.
export const feePeriodLabel = (start: string, end: string): string => {
  const s = dayjs(start)
  const e = dayjs(end)
  const wholeMonths = s.date() === 1 && e.isSame(e.endOf('month'), 'day')
  const months = e.diff(s, 'month') + 1
  if (wholeMonths && s.year() === e.year()) {
    if (months === 3 && s.month() % 3 === 0) return `Q${s.month() / 3 + 1} ${s.year()}`
    if (months === 6 && s.month() % 6 === 0) return `H${s.month() / 6 + 1} ${s.year()}`
    if (months === 12 && s.month() === 0) return String(s.year())
  }
  return `${s.format(DATE_FORMAT)} – ${e.format(DATE_FORMAT)}`
}

const signed = (value: number | null | undefined) => {
  const v = value ?? 0
  if (Math.abs(v) < 0.005) return <Text type='secondary'>—</Text>
  if (v < 0)
    return (
      <Text type='danger'>
        (<NumberText value={Math.abs(v)} />)
      </Text>
    )
  return <NumberText value={v} />
}

const hasDrift = (r: FeeHistoryRow): boolean =>
  r.prog__basis_source === 'snapshot' && r.prog__drift != null && Math.abs(r.prog__drift) >= 0.005

const signedChange = (value: number) => (
  <span className={value < 0 ? 'text-red-500' : undefined}>
    {value > 0 ? '+' : '−'}
    <NumberText value={Math.abs(value)} />
  </span>
)

interface DriftLine {
  key: string
  label: string
  then: React.ReactNode
  now: React.ReactNode
  change: React.ReactNode
}

// then-vs-today lines for the fields a terms change can move; unchanged
// fields are left out so the block reads as "what moved", period fee always
const driftLines = (then: FeeBasis, now: FeeBasis): DriftLine[] => {
  const money = (key: string, label: string, a: number | null | undefined, b: number | null | undefined) =>
    a != null && b != null && (key === 'period_fee' || Math.abs(b - a) >= 0.005)
      ? [{ key, label, then: <NumberText value={a} />, now: <NumberText value={b} />, change: signedChange(b - a) }]
      : []
  const lines: DriftLine[] = []
  if ((then.basis_label ?? '') !== (now.basis_label ?? '')) {
    lines.push({ key: 'basis', label: 'Basis', then: then.basis_label ?? '—', now: now.basis_label ?? '—', change: '' })
  }
  lines.push(...money('basis_amount', 'Basis amount', then.basis_amount, now.basis_amount))
  if (
    then.fee_paying_share != null &&
    now.fee_paying_share != null &&
    Math.abs(now.fee_paying_share - then.fee_paying_share) >= 0.0005
  ) {
    const pts = (now.fee_paying_share - then.fee_paying_share) * 100
    lines.push({
      key: 'share',
      label: 'Fee-paying share',
      then: formatPercent(then.fee_paying_share),
      now: formatPercent(now.fee_paying_share),
      change: (
        <span
          className={pts < 0 ? 'text-red-500' : undefined}
        >{`${pts > 0 ? '+' : '−'}${Math.abs(pts).toFixed(1)} pts`}</span>
      )
    })
  }
  lines.push(...money('fee_paying_basis', 'Fee-paying basis', then.fee_paying_basis, now.fee_paying_basis))
  lines.push(...money('period_fee', 'Period fee', then.period_fee, now.period_fee))
  return lines
}

const DriftComparison = ({ row }: { row: FeeHistoryRow }) => {
  if (!row.current_basis) return null
  const booked = row.transaction_statuses__label === 'Booked'
  return (
    <div className='mb-3 rounded border border-yellow-300 bg-yellow-50 p-2'>
      <Text strong style={{ fontSize: 12 }}>
        Terms changed since this fee was calculated
      </Text>
      <Table
        className='mt-1'
        size='small'
        pagination={false}
        rowKey='key'
        dataSource={driftLines(row.basis, row.current_basis)}
        columns={[
          { title: '', dataIndex: 'label', key: 'label' },
          {
            title: booked ? 'At booking' : 'At last save',
            dataIndex: 'then',
            key: 'then',
            align: 'right' as const
          },
          { title: 'Today', dataIndex: 'now', key: 'now', align: 'right' as const },
          { title: 'Change', dataIndex: 'change', key: 'change', align: 'right' as const }
        ]}
      />
      <Text type='secondary' style={{ fontSize: 12 }}>
        {booked
          ? 'The booked amount stays as posted; the next fee booking trues up the difference.'
          : 'Save the transaction to recalculate it on today’s terms.'}
      </Text>
    </div>
  )
}

interface FeeHistorySectionProps {
  rows: FeeHistoryRow[]
  loading: boolean
  onOpenTransaction: (row: FeeHistoryRow) => void
  /** The Export file's name stem, e.g. `<fund> Fee history`. */
  exportName?: string
}

export default function FeeHistorySection({
  rows,
  loading,
  onOpenTransaction,
  exportName = 'Fee history'
}: FeeHistorySectionProps) {
  const columns = useMemo(
    () => [
      {
        title: 'Fee period',
        key: 'period',
        render: (_: unknown, r: FeeHistoryRow) => (
          <button
            type='button'
            className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
            onClick={() => onOpenTransaction(r)}
          >
            {feePeriodLabel(r.fund_transactions__fee_period_start, r.fund_transactions__fee_period_end)}
          </button>
        )
      },
      {
        title: 'Status',
        dataIndex: 'transaction_statuses__label',
        key: 'status',
        render: (v: string | null) => (v ? <Tag color={v === 'Booked' ? 'green' : 'blue'}>{v}</Tag> : '—')
      },
      {
        title: 'Basis',
        key: 'basis',
        render: (_: unknown, r: FeeHistoryRow) => r.basis.basis_label ?? '—'
      },
      {
        title: 'Basis amount',
        key: 'basis_amount',
        align: 'right' as const,
        render: (_: unknown, r: FeeHistoryRow) => <NumberText value={r.basis.basis_amount} />
      },
      {
        title: 'Fee-paying',
        key: 'fee_paying_share',
        align: 'right' as const,
        render: (_: unknown, r: FeeHistoryRow) => formatPercent(r.basis.fee_paying_share)
      },
      {
        title: 'Period fee',
        dataIndex: 'prog__period_fee',
        key: 'period_fee',
        align: 'right' as const,
        render: (v: number | null) => <NumberText value={v} />
      },
      {
        title: 'True-up',
        dataIndex: 'prog__true_up',
        key: 'true_up',
        align: 'right' as const,
        render: (v: number | null) => signed(v)
      },
      {
        title: 'Amount',
        dataIndex: 'prog__amount',
        key: 'amount',
        align: 'right' as const,
        render: (v: number | null) => signed(v)
      },
      {
        title: 'Source',
        dataIndex: 'prog__basis_source',
        key: 'source',
        render: (v: FeeHistoryRow['prog__basis_source'], r: FeeHistoryRow) =>
          v === 'snapshot' ? (
            <span className='whitespace-nowrap'>
              <Tooltip title='Basis saved when the transaction was generated'>
                <Tag color='green'>Snapshot</Tag>
              </Tooltip>
              {hasDrift(r) ? (
                <Tooltip
                  title={
                    <span>
                      Period fee on today's terms: <NumberText value={r.prog__current_period_fee} /> (
                      {signedChange(r.prog__drift ?? 0)})
                    </span>
                  }
                >
                  <Tag color='gold'>Terms changed</Tag>
                </Tooltip>
              ) : null}
            </span>
          ) : (
            <Tooltip title="No saved basis: recalculated with today's terms, so it may differ from what was booked">
              <Tag color='gold'>Recomputed</Tag>
            </Tooltip>
          )
      }
    ],
    [onOpenTransaction]
  )

  return (
    <FormSection
      title='Fee history'
      titleColor='orange'
      loading={loading}
      headerActions={
        <ExportRowsButton
          name={exportName}
          rows={rows}
          columns={[
            {
              title: 'Fee period',
              value: (r) => feePeriodLabel(r.fund_transactions__fee_period_start, r.fund_transactions__fee_period_end)
            },
            { title: 'Period start', value: (r) => r.fund_transactions__fee_period_start, kind: 'date' },
            { title: 'Period end', value: (r) => r.fund_transactions__fee_period_end, kind: 'date' },
            { title: 'Status', value: (r) => r.transaction_statuses__label },
            { title: 'Basis', value: (r) => r.basis.basis_label },
            { title: 'Basis amount', value: (r) => r.basis.basis_amount },
            { title: 'Fee-paying', value: (r) => r.basis.fee_paying_share, kind: 'percent' },
            { title: 'Period fee', value: (r) => r.prog__period_fee },
            { title: 'True-up', value: (r) => r.prog__true_up },
            { title: 'Amount', value: (r) => r.prog__amount },
            { title: 'Source', value: (r) => (r.prog__basis_source === 'snapshot' ? 'Snapshot' : 'Recomputed') },
            // the Source cell's gold tag and its tooltip: today's period fee and the drift
            { title: 'Terms changed', value: (r) => (hasDrift(r) ? 'Yes' : null) },
            { title: "Period fee on today's terms", value: (r) => r.prog__current_period_fee },
            { title: 'Drift', value: (r) => r.prog__drift }
          ]}
        />
      }
    >
      <Table
        size='small'
        pagination={false}
        rowKey='fund_transactions__id'
        dataSource={rows}
        columns={columns}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: 'No fee transactions yet' }}
        expandable={{
          expandedRowRender: (r: FeeHistoryRow) => (
            <div className='py-1'>
              {r.prog__basis_source === 'recomputed' ? (
                <div className='mb-2'>
                  <Text type='warning' style={{ fontSize: 12 }}>
                    Recalculated with today's terms, so it may differ from what was booked.
                  </Text>
                </div>
              ) : null}
              {hasDrift(r) ? <DriftComparison row={r} /> : null}
              {hasDrift(r) ? (
                <div className='mb-1'>
                  <Text type='secondary' style={{ fontSize: 12 }}>
                    Basis when calculated:
                  </Text>
                </div>
              ) : null}
              <FeeBasisCard basis={r.basis} />
            </div>
          )
        }}
      />
      <div className='mt-1'>
        <Text type='secondary' style={{ fontSize: 12 }}>
          Amount is what the transaction books: all fees due to date minus fees already booked, so True-up shows any
          catch-up of earlier periods. Expand a period for its calculation basis.
        </Text>
      </div>
    </FormSection>
  )
}
